// Components
import CheckboxInput from "components/inputs/checkbox"
import DropdownInput from "components/inputs/dropdown"
import ListboxInput from "components/inputs/listbox"
import { CashFlowReport_s } from 'components/property/reports/report-styles'
import { parseDate } from "data/lib/dates"
import propertyReportService from "data/reports-property"
// Data
import underwritingService from "data/underwriting"
import _ from 'lodash'
import calcService from "data/calc"
import React, { FC, useEffect, useState } from "react"
// Styles
import { ButtonWithPress_s, Button_s, Label_s } from "styles/app-styles"
import { InputNumber_s } from "styles/input-styles"
import { DateText_s, ReportForm_s } from "styles/report-styles"
import TenantGrid from './tenant-grid'

interface FullTenantDetailProps {
  property: object
}

function useForceUpdate(){
  const [value, setValue] = useState(0); // integer state
  return () => setValue(value => value + 1); // update the state to force render
}

const FullTenantDetail: FC<FullTenantDetailProps> = (props) => {
  const [start_month, setStartMonth] = useState<string>(underwritingService.getReportBeginDefault(true, null, true))
  const [term, setTerm] = useState<number>(propertyReportService.getReportTermDefault())
  const [periodicity, setPeriodicity] = useState<number>(12)
  const [amount_type, setAmtType] = useState<string>("amount")
  const [totals, setTotals] = useState<boolean>(false);
  const [tenants, setTenants] = useState<number[]>([]);
  const [reports, setReports] = useState<string[]>(['occupied_area', 'scheduled_base_rent', 'scheduled_recoveries', 'scheduled_percentage_rent', 'tenant_improvement', 'leasing_commissions']);
  const forceUpdate = useForceUpdate();

  const periodicityOptions = [
    { label: 'Annual', value: 12 },
    { label: 'Monthly', value: 1 },
    { label: 'Quarterly', value: 3 }
  ]
  
  useEffect(() => {
    if (periodicity === 12 && totals === true){
      setTotals(false);
    }
  }, [periodicity, totals])

  useEffect(() => {
    setStartMonth(underwritingService.getReportBeginDefault(true, null, true));
    setTerm(propertyReportService.getReportTermDefault());
  }, [props['property']])

  function submit():void {
    propertyReportService.propertyReport({
      report: 'tenant_detail',
      params: {
        amount_type,
        period: periodicity,
        start_month: underwritingService.toISOdate(start_month),
        reports,  
        tenants: tenants.map(tenant => tenant['rentrollid']),
        term, 
        totals
      }
    });
  }

  function updateDate(date:string):void {
    const { adjustedDate, validatedTerm } = propertyReportService.updateDate(date, term);
    setStartMonth(adjustedDate);
    if (validatedTerm) setTerm(validatedTerm);
    forceUpdate();
  }

  function updateTerm(term:number):void {
    const adjustedTerm:number|boolean = propertyReportService.validateTerm(term, start_month);
    if (typeof adjustedTerm === 'number'){
      setTerm(adjustedTerm)
    } else {
      setTerm(term);
    }
    forceUpdate();
  }

  const disabled = !calcService.calcCheck(props.property)
  const buttonText = disabled ? 'Calculation Required' : 'Run Report'


  const reportsOptions = [
    {label: 'Occupied Area', value: 'occupied_area'},
    {label: 'Vacant Area', value: 'vacant_area'},
    {label: 'Market Rent', value: 'market_rent'},
    {label: 'Market Recoveries', value: 'market_recoveries'},
    {label: 'Potential Base Rent', value: 'base_rent'},
    {label: 'Base Rent - Vacancy Loss', value: 'base_rent_vacancy_loss'},
    {label: 'Base Rent - Abatement', value: 'base_rent_abatement'},
    {label: 'Scheduled Base Rent', value: 'scheduled_base_rent'},
    {label: 'Potential Recoveries', value: 'recoveries'},
    {label: 'Recoveries - Vacancy Loss', value: 'recoveries_vacancy_loss'},
    {label: 'Recoveries - Abatement', value: 'recoveries_abatement'},
    {label: 'Scheduled Recoveries', value: 'scheduled_recoveries'},
    {label: 'Potential Misc Revenue', value: 'misc_revenue'},
    {label: 'Misc Revenue - Vacancy Loss', value: 'misc_revenue_vacancy_loss'},
    {label: 'Misc Revenue - Abatement', value: 'misc_revenue_abatement'},
    {label: 'Scheduled Misc Revenue', value: 'scheduled_misc_revenue'},
    {label: 'Potential Percentage Rent', value: 'percentage_rent'},
    {label: 'Percentage Rent - Vacancy Loss', value: 'percentage_rent_vacancy_loss'},
    {label: 'Percentage Rent - Abatement', value: 'percentage_rent_abatement'},
    {label: 'Scheduled Percentage Rent', value: 'scheduled_percentage_rent'},
    {label: 'Tenant Improvement', value: 'tenant_improvement'},
    {label: 'Leasing Commissions', value: 'leasing_commissions'},
    {label: 'Other Tenant Capital', value: 'other_tenant_capital'},
    {label: 'Sales Volume', value: 'sales_volume'}
  ];

  return (
    <CashFlowReport_s>
      <ButtonWithPress_s primary onClick={submit} style={{marginBottom: 20}} disabled={disabled}>{buttonText}</ButtonWithPress_s>
      <ReportForm_s>
        <div>
          <Label_s>Start Date</Label_s>
          <DateText_s withoutDays value={parseDate(start_month).format('MM/DD/YYYY')} onChange={updateDate} onBlur={updateDate} persist={updateDate} />
        </div>
        <div>
          <Label_s>Term</Label_s>
          <InputNumber_s value={term} onChange={(e) => updateTerm(e.value)} maxFractionDigits={0} />
        </div>
        <div>
          <Label_s>Periodicity</Label_s>
          <DropdownInput
            persist={(field:string, val:number)=>setPeriodicity(val)}
            field="periodicity"
            options={periodicityOptions}
            value={periodicity}
          />
        </div>
        <div>
          <Label_s>Display Annual Totals</Label_s>
          <CheckboxInput
            disabled={periodicity === 12}
            field="totals"
            value={totals}
            persist={(field:string, val:boolean) => setTotals(val)}
          />
        </div>
        <div>
          <Label_s>Amount Type</Label_s>
          <DropdownInput
            className="line-item-detail-input"
            persist={(field:string, val:string)=>setAmtType(val)}
            field="amount_type"
            options={[{label: 'Amount', value: 'amount'},{label: 'Per Area', value: 'per_area'}]}
            value={amount_type}
          />
        </div>
        </ReportForm_s>
        <ReportForm_s>
        <TenantGrid selected={tenants} setSelected={setTenants} />
        <div style={{width: '250px'}}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10}}>
            <Label_s>Tenant Detail Type</Label_s>
            <Button_s onClick={()=>{ reports.length === reportsOptions.length ? setReports([]) : setReports(reportsOptions.map(option => option.value))}}>
              {reports.length === reportsOptions.length ? 'Deselect All' : 'Select All'}
            </Button_s>
          </div>
          <ListboxInput 
          field="reports" 
          value={reports} 
          options={reportsOptions} 
          persist={(field:string, val:string[])=>setReports(val)} 
          multiple={true} 
          />
        </div>
      </ReportForm_s>
    </CashFlowReport_s>
  )
}

export default FullTenantDetail
